/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import {PackageCategory} from "types/enums/PackageCategory";

export function getQueryParams() {
    function identity(e) {
        return e;
    }

    function toKeyValue(params, param) {
        const [key, value] = param.split("=");

        params[key] = params[key] ? [value].concat(params[key]) : value;
        return params;
    }

    return decodeURIComponent(window.location.search).replace(/^\?/, "").split("&").filter(identity).reduce(toKeyValue, {});
}

/**
 * @description
 * Takes an Array<V>, and a grouping function,
 * and returns a Map of the array grouped by the grouping function.
 *
 * @param list An array of type V.
 * @param keyGetter A Function that takes the the Array type V as an input, and returns a value of type K.
 *                  K is generally intended to be a property key of V.
 *
 * @returns Map of the array grouped by the grouping function.
 */
//export function groupBy<K, V>(list: Array<V>, keyGetter: (input: V) => K): Map<K, Array<V>> {
//    const map = new Map<K, Array<V>>();
export function groupBy(list, keyGetter) {
    const map = new Map();
    list.forEach(item => {
        const key = keyGetter(item);
        const collection = map.get(key);
        if (!collection) {
            map.set(key, [item]);
        } else {
            collection.push(item);
        }
    });
    return map;
}

export const getArrayGroupBy = <T>(array: T[], key: keyof T): Record<string, T[]> => {
    return (array ?? []).reduce(
        (result, currentValue) => {
            const groupKey = currentValue[key] as unknown as string;
            if (!result[groupKey]) {
                result[groupKey] = [];
            }
            result[groupKey].push(currentValue);
            return result;
        },
        {} as Record<string, T[]>
    );
};

/**
 * A simple forEach() implementation for Arrays, Objects and NodeLists
 * @private
 * @param {Array|Object|NodeList} collection Collection of items to iterate
 * @param {Function} callback Callback function for each iteration
 * @param {Array|Object|NodeList} scope Object/NodeList/Array that forEach is iterating over (aka `this`)
 * @return {Array|Object}
 */
export const forEach = function (collection, callback, scope = null) {
    const result = [];
    if (collection === null) {
        return result;
    }
    if (Object.prototype.toString.call(collection) === "[object Object]") {
        for (const prop in collection) {
            if (Object.prototype.hasOwnProperty.call(collection, prop)) {
                // @ts-ignore
                result.push(callback.call(scope, collection[prop], prop, collection));
            }
        }
    } else {
        for (let i = 0, len = collection.length; i < len; i++) {
            // @ts-ignore
            result.push(callback.call(scope, collection[i], i, collection));
        }
    }
    return result;
};

//https://egghead.io/lessons/javascript-redux-persisting-the-state-to-the-local-storage
export const loadState = () => {
    try {
        const serializedState = localStorage.getItem("state");
        if (serializedState === null) {
            return undefined;
        }
        const jsonState = JSON.parse(serializedState);

        return jsonState;
    } catch (err) {
        return undefined;
    }
};

export const saveState = state => {
    try {
        const trimmedState = {...state};
        delete trimmedState["ajaxLoading"];
        const serializedState = JSON.stringify(trimmedState);
        localStorage.setItem("state", serializedState);
    } catch (err) {
        // ignore write errors
    }
};

export const profile = (what, callback) => {
    const startTime = performance.now();

    // Do the normal stuff for this function
    callback();

    const duration = performance.now() - startTime;
    console.log(`${what} took ${duration}ms`);
};

export const Url = {
    serialize: function (obj, prefix) {
        const str = [];
        for (const p in obj) {
            if (obj.hasOwnProperty(p)) {
                const k = prefix ? prefix + "[" + p + "]" : p;
                const v = obj[p];
                // @ts-ignore
                str.push(
                    // @ts-ignore
                    v !== null && typeof v === "object" && Object.keys(v).length > 0 ? Url.serialize(v, k) : encodeURIComponent(k) + "=" + encodeURIComponent(v)
                );
            }
        }
        return str.join("&");
    },
};

export const selectMapper = (labelName, valueName) => {
    return item => ({label: item[labelName], value: item[valueName]});
};

export const bookingLoginModalCustomStyles = {
    content: {
        top: "50%",
        left: "50%",
        right: "auto",
        bottom: "auto",
        marginRight: "-50%",
        transform: "translate(-50%, -50%)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        borderWidth: "0",
        padding: "0",
        margin: "0",
        background: "transparent",
    },
    overlay: {
        backgroundColor: "rgba(0,0,0,0.7)",
        padding: "30px 15px",
        zIndex: 10000,
    },
};

export const getDriversCountByCategory = (category: PackageCategory) => {
    switch (category) {
        case PackageCategory.Event:
        case PackageCategory.EventS:
            return {
                adult: {minimum: 5, maximum: 50},
                child: {minimum: 0, maximum: 0},
                package: {minimum: 5, maximum: 50},
            };
        case PackageCategory.EventM:
            return {
                adult: {minimum: 10, maximum: 50},
                child: {minimum: 0, maximum: 0},
                package: {minimum: 10, maximum: 50},
            };
        case PackageCategory.EventL:
            return {
                adult: {minimum: 10, maximum: 50},
                child: {minimum: 0, maximum: 0},
                package: {minimum: 10, maximum: 50},
            };
        case PackageCategory.EventMegaFun:
            return {
                adult: {minimum: 4, maximum: 100},
                child: {minimum: 0, maximum: 0},
                package: {minimum: 4, maximum: 100},
            };
        case PackageCategory.EventNonPlusUltra:
            return {
                adult: {minimum: 8, maximum: 100},
                child: {minimum: 0, maximum: 0},
                package: {minimum: 8, maximum: 100},
            };
        case PackageCategory.PolterabendMegaFun:
            return {
                adult: {minimum: 4, maximum: 100},
                child: {minimum: 0, maximum: 0},
                package: {minimum: 4, maximum: 100},
            };
        case PackageCategory.PolterabendNonPlusUltra:
            return {
                adult: {minimum: 8, maximum: 100},
                child: {minimum: 0, maximum: 0},
                package: {minimum: 8, maximum: 100},
            };

        case PackageCategory.Action:
            return {
                adult: {minimum: 1, maximum: 5},
                child: {minimum: 1, maximum: 5},
                package: {minimum: 2, maximum: 6},
            };

        case PackageCategory.School:
            return {
                adult: {minimum: 1, maximum: 5},
                child: {minimum: 9, maximum: 100},
                package: {minimum: 10, maximum: 100},
            };
        case PackageCategory.MegaFunSchool:
            return {
                adult: {minimum: 1, maximum: 10},
                child: {minimum: 8, optimum: 9, maximum: 100},
                package: {minimum: 10, maximum: 100},
            };
        case PackageCategory.SchoolTrophy:
            return {
                adult: {minimum: 1, maximum: 10},
                child: {minimum: 35, optimum: 40, maximum: 100},
                package: {minimum: 36, maximum: 100},
            };
        case PackageCategory.FerienPass:
            return {
                adult: {minimum: 1, maximum: 50},
                child: {minimum: 3, maximum: 50},
                package: {minimum: 4, maximum: 50},
            };
        case PackageCategory.FerienPassB:
            return {
                adult: {minimum: 0, maximum: 0},
                child: {minimum: 1, maximum: 50},
                package: {minimum: 1, maximum: 50},
            };
        case PackageCategory.Kinder:
            return {
                adult: {minimum: 1, maximum: 50},
                child: {minimum: 4, maximum: 50},
                package: {minimum: 5, maximum: 50},
            };
        case PackageCategory.GigaFun:
            return {
                adult: {minimum: 8, maximum: 100},
                child: {minimum: 0, maximum: 0},
                package: {minimum: 8, maximum: 100},
            };
        case PackageCategory.NonPlusUltra:
            return {
                adult: {minimum: 8, maximum: 100},
                child: {minimum: 0, maximum: 0},
                package: {minimum: 8, maximum: 100},
            };
        case PackageCategory.HappyKids:
        case PackageCategory.HappyKidsPlus:
            return {
                adult: {minimum: 1, maximum: 50},
                child: {minimum: 4, maximum: 50},
                package: {minimum: 5, maximum: 50},
            };
        case PackageCategory.MegaFun:
        case PackageCategory.HappyParty:
            return {
                adult: {minimum: 4, maximum: 100},
                child: {minimum: 0, maximum: 0},
                package: {minimum: 4, maximum: 100},
            };
        case PackageCategory.HappyPartyPlus:
        case PackageCategory.HappyPartyVIP:
            return {
                adult: {minimum: 8, maximum: 100},
                child: {minimum: 0, maximum: 0},
                package: {minimum: 8, maximum: 100},
            };
        default:
            return {
                adult: {minimum: 1, maximum: 50},
                child: {minimum: 0, maximum: 50},
                package: {minimum: 1, maximum: 50},
            };
    }
};

export const getStartAndEndOfWeek = (date = new Date()) => {
    const day = date.getDay();
    const diffToMonday = day === 0 ? -6 : 1 - day;

    const startOfWeek = new Date(date.getTime());
    startOfWeek.setDate(date.getDate() + diffToMonday);

    const endOfWeek = new Date(startOfWeek.getTime());
    endOfWeek.setDate(startOfWeek.getDate() + 6);

    const formatDate = d => {
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
    };

    return {
        startOfWeek: formatDate(startOfWeek),
        endOfWeek: formatDate(endOfWeek),
    };
};

export const getFirstDayOfWeekByDirection = (date = new Date(), direction = "next") => {
    const currentDate = new Date(date);
    const day = currentDate.getDay();

    const diffToMonday = (day === 0 ? -6 : 1) - day;
    const weekAdjustment = direction === "next" ? 7 : direction === "previous" ? -7 : 0;

    currentDate.setDate(currentDate.getDate() + diffToMonday + weekAdjustment);
    currentDate.setHours(0, 0, 0, 0);
    return currentDate;
};
